import React, { useEffect, useState, createContext, useContext } from 'react';
import { fetchAndActivate, getAll } from 'firebase/remote-config';
import { remoteConfig } from '@/services/firebase';
import { FeatureFlagsParsed, FeatureFlagsRaw } from './featureFlags.types';
import { parseFeatureFlags } from './featureFlags.parser';
import { getFlagsSnapshot, setFlagsSnapshot } from './snapshot';

function parseAllParameters(
    parameters: ReturnType<typeof getAll>,
): FeatureFlagsParsed {
    const raw = Object.create(null) as FeatureFlagsRaw;
    for (const key of Object.keys(parameters)) {
        raw[key] = parameters[key].asString();
    }
    return parseFeatureFlags(raw);
}

function readRemoteConfigFlags(): FeatureFlagsParsed {
    const parameters = getAll(remoteConfig);
    if (Object.keys(parameters).length > 0) {
        return parseAllParameters(parameters);
    }

    return {};
}

try {
    // Inicializa o snapshot com valores em cache sem depender do provider montado.
    setFlagsSnapshot(readRemoteConfigFlags());
} catch {
    // Se o Remote Config ainda não estiver pronto, mantém snapshot vazio.
}

const defaultFlags: FeatureFlagsParsed = getFlagsSnapshot();

const FlagsContext = createContext<FeatureFlagsParsed>(defaultFlags);

export const useFlags = (): FeatureFlagsParsed => useContext(FlagsContext);
export { getFlagsSnapshot };

export function FlagsProvider({ children }: { children: React.ReactNode }) {
    const [flags, setFlags] = useState<FeatureFlagsParsed>(defaultFlags);

    async function loadFlags() {
        try {
            await fetchAndActivate(remoteConfig);
            const parsedFlags = readRemoteConfigFlags();
            setFlagsSnapshot(parsedFlags);
            setFlags(parsedFlags);
        } catch (error) {
            console.error('Error fetching feature flags:', error);
        }
    }

    useEffect(() => {
        loadFlags();
    }, []);

    return (
        <FlagsContext.Provider value={flags}>{children}</FlagsContext.Provider>
    );
}
